import { freezeRequest } from './generation-request.js';

// A missing result is work that has not reached submission. Submission emits
// its own state before awaiting the SDK, including its uncertain-delivery path.
export function isRetryableResult(result) {
  if (!result) return true;
  if (result.unverified) return false;
  if (['error', 'canceled', 'cancelled'].includes(result.status)) return true;
  return ['unsubmitted', 'paused'].includes(result.status) && !result.projectId;
}

export function selectRetryRequest(request, results = {}) {
  const slots = request.slots.filter(slot => isRetryableResult(results[slot.id]));
  if (!slots.length) {
    const error = new Error('There are no verified failed or unsubmitted images to retry. Images still running or awaiting recovery will not be submitted again.');
    error.code = 'NO_RETRYABLE_SLOTS';
    throw error;
  }
  return freezeRequest({ ...request, slots });
}

// Retain the complete manifest while an attempt submits only some of its slots.
// A reviewed recovery variant may update a failed slot's billing/network/model.
export function mergeImageRequests(previous, incoming) {
  if (!previous || previous.id !== incoming.id) return incoming;
  const updated = new Map(incoming.slots.map(slot => [slot.id, slot]));
  const slots = previous.slots.map(slot => updated.get(slot.id) || slot);
  for (const slot of incoming.slots) {
    if (!previous.slots.some(original => original.id === slot.id)) slots.push(slot);
  }
  return freezeRequest({ ...previous, ...incoming, slots,
    modelIds: [...new Set(slots.map(slot => slot.modelId))],
    totalBatches: Math.max(previous.totalBatches, incoming.totalBatches) });
}

export function resultsFromBatches(batches = []) {
  return Object.fromEntries(batches.flat().filter(image => image?.slotId)
    .map(image => [image.slotId, image]));
}

// An explicitly chosen replacement model resolves its own concrete defaults,
// but its output shape and native input bytes come from the saved slot.
export function savedModelInputs(slot) {
  const params = slot.projectConfig;
  return {
    width: params.width, height: params.height, network: params.network,
    preset: 'final', modelOverrides: { outputFormat: params.outputFormat },
    disableNSFWFilter: params.disableNSFWFilter === true,
    referenceAssets: (params.contextImages || []).map((blob, index) => ({
      id: `${slot.id}:reference:${index}`, blob, enabled: true
    })),
    guideImage: params.startingImage ? { blob: params.startingImage } : null,
    guideStrength: params.startingImageStrength ?? 0.5
  };
}

// Grid coordinates belong to the original manifest. Sparse retries never
// compact batches or replace successful tile objects, selections or ratings.
export function mergeRequestBatches(request, { batches = [], results = {} } = {}) {
  if (!request) return batches;
  const grid = Array.from({ length: request.totalBatches }, (_, index) =>
    Array.from({ length: request.imagesPerPrompt }, (_, imageIndex) => batches[index]?.[imageIndex] || null));
  for (const slot of request.slots) {
    grid[slot.batchIndex][slot.imageIndex] = mergeSlotResult(request.id, slot,
      grid[slot.batchIndex][slot.imageIndex], results[slot.id]);
  }
  return grid;
}

export function mergeSlotResult(requestId, slot, existing, result) {
  if (existing?.status === 'done' && existing.url) {
    // A sensitive label that arrives after the tile is done (a sync following a
    // replayed completion) still applies; nothing else about a done tile changes.
    return result?.sensitive === true && existing.sensitive !== true ? { ...existing, sensitive: true } : existing;
  }
  const metadata = {
    slotId: slot.id, manifestId: requestId,
    batchIndex: slot.batchIndex, imageIndex: slot.imageIndex,
    imageId: `batch-${slot.batchIndex}-img-${slot.imageIndex}`,
    batchId: `batch-${slot.batchIndex}`, prompt: slot.prompt,
    model: slot.modelId, requestedModel: slot.modelId,
    seed: slot.projectConfig.seed, width: slot.projectConfig.width,
    height: slot.projectConfig.height, projectConfig: slot.projectConfig
  };
  if (!result) return existing || { ...metadata, status: 'unsubmitted', url: null, selected: false };
  return { ...metadata, ...existing, ...result,
    url: ['error', 'canceled', 'cancelled'].includes(result.status) ? null
      : result.imageUrl || result.previewUrl || result.url || null,
    selected: existing?.selected ?? false, rating: existing?.rating ?? result.rating,
    completedAt: result.status === 'done' ? result.completedAt || result.timestamp : existing?.completedAt,
    unverified: result.unverified === true };
}
